import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/campus/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageSquare, Phone, CheckSquare, User, Home, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function StudentDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("home");
  const [convos, setConvos] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [colleges, setColleges] = useState([]);
  const nav = useNavigate();

  useEffect(()=>{
    if (!loading && (!user || user.role !== "student")) { nav("/auth?mode=login"); return; }
    if (user) {
      api.myConversations().then(setConvos).catch(()=>{});
      api.myDecisions().then(setDecisions).catch(()=>{});
      api.listColleges().then(c=>setColleges(c.slice(0,3))).catch(()=>{});
    }
  }, [user, loading]);

  if (!user) return null;
  const chats = convos.filter(c=>c.type==="chat");
  const calls = convos.filter(c=>c.type==="audio_call");
  const greeting = (h=>h<12?"Good morning":h<17?"Good afternoon":"Good evening")(new Date().getHours());

  const Tab = ({id, icon, label}) => (
    <button data-testid={`tab-${id}`} onClick={()=>setTab(id)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm w-full text-left transition ${tab===id?"bg-[#2563EB] text-white":"text-slate-300 hover:bg-white/5"}`}>{icon}{label}</button>
  );

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <Navbar/>
      <div className="pt-20 px-6 md:px-12 max-w-7xl mx-auto grid md:grid-cols-[240px,1fr] gap-6 pb-20">
        <aside className="glass rounded-2xl p-3 h-fit md:sticky md:top-24 space-y-1">
          <Tab id="home" icon={<Home size={16}/>} label="Home"/>
          <Tab id="chats" icon={<MessageSquare size={16}/>} label="My Chats"/>
          <Tab id="calls" icon={<Phone size={16}/>} label="My Calls"/>
          <Tab id="decisions" icon={<CheckSquare size={16}/>} label="My Decisions"/>
          <Tab id="profile" icon={<User size={16}/>} label="Profile"/>
        </aside>
        <main>
          {tab==="home" && (
            <div className="space-y-6">
              <div>
                <h1 className="font-display text-3xl font-bold">{greeting}, {user.full_name?.split(" ")[0]} 👋</h1>
                <p className="text-slate-400 mt-1">Ready to find your perfect college?</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <StatCard label="Chats done" value={chats.filter(c=>c.status==="completed").length}/>
                <StatCard label="Calls done" value={calls.filter(c=>c.status==="completed").length}/>
                <StatCard label="Decisions" value={decisions.length}/>
              </div>
              <div>
                <h2 className="font-display text-xl font-bold mb-3">Recommended Colleges</h2>
                <div className="grid md:grid-cols-3 gap-4">
                  {colleges.map(c=>(
                    <Link key={c.slug} to={`/college/${c.slug}`} className="glass rounded-2xl p-4 hover:-translate-y-1 transition">
                      <div className="font-display font-bold">{c.name}</div>
                      <div className="text-xs text-slate-400 mt-1">{c.city}, {c.state}</div>
                      <div className="text-xs text-[#60A5FA] mt-3 inline-flex items-center gap-1">Explore <ArrowRight size={12}/></div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
          {tab==="chats" && <ConvoList items={chats} type="chat"/>}
          {tab==="calls" && <ConvoList items={calls} type="audio_call"/>}
          {tab==="decisions" && (
            <div>
              <h1 className="font-display text-2xl font-bold mb-4">My Decisions</h1>
              {decisions.length===0 && <div className="text-slate-400 text-sm">No decisions yet. Explore colleges and click "I've Decided" when ready.</div>}
              <div className="space-y-3">{decisions.map(d=>(
                <div key={d.id} className="glass rounded-2xl p-5">
                  <div className="flex justify-between"><div className="font-semibold text-lg">{d.college_name}</div><span className="text-xs px-2 py-1 rounded-full bg-amber-500/15 text-amber-400 capitalize">{d.status.replace("_"," ")}</span></div>
                  <div className="text-xs text-slate-400 mt-2">Decided: {new Date(d.decided_at).toLocaleDateString()} · Preferred: {d.preferred_contact_time}</div>
                  <div className="text-xs text-emerald-400 mt-2">📞 Our team will reach you within 24h</div>
                </div>
              ))}</div>
            </div>
          )}
          {tab==="profile" && <ProfileView/>}
        </main>
      </div>
    </div>
  );
}

const StatCard = ({label, value}) => <div className="glass rounded-2xl p-5"><div className="text-xs text-slate-400">{label}</div><div className="font-display text-3xl font-bold mt-1">{value}</div></div>;

function ConvoList({items, type}){
  return (
    <div>
      <h1 className="font-display text-2xl font-bold mb-4 capitalize">My {type==="chat"?"Chats":"Calls"}</h1>
      {items.length===0 && <div className="text-slate-400 text-sm">No {type==="chat"?"chats":"calls"} yet.</div>}
      <div className="space-y-3">{items.map(c=>(
        <Link key={c.id} to={type==="chat"?`/chat/${c.id}`:`/call/${c.id}`} className="block glass rounded-2xl p-4 hover:-translate-y-0.5 transition">
          <div className="flex justify-between items-center"><div><div className="font-semibold">{c.other_name}</div><div className="text-xs text-slate-400">{c.college_name} · {new Date(c.created_at).toLocaleString()}</div></div>
          <span className={`text-xs px-2 py-1 rounded-full capitalize ${c.status==="completed"?"bg-emerald-500/15 text-emerald-400":"bg-blue-500/15 text-blue-400"}`}>{c.status}</span></div>
        </Link>
      ))}</div>
    </div>
  );
}

function ProfileView(){
  const { user, profile } = useAuth();
  return (
    <div>
      <h1 className="font-display text-2xl font-bold mb-4">Profile</h1>
      <div className="glass rounded-2xl p-6 space-y-3">
        <Row k="Name" v={user.full_name}/><Row k="Email" v={user.email}/><Row k="Phone" v={user.phone}/>
        {profile && <><Row k="State" v={profile.state}/><Row k="Stream" v={profile.stream}/><Row k="12th %" v={profile.percentage}/><Row k="Budget" v={profile.budget_range}/></>}
      </div>
    </div>
  );
}
const Row = ({k,v}) => <div className="flex justify-between text-sm"><span className="text-slate-400">{k}</span><span>{v}</span></div>;
